<script setup lang="ts">
import { isAppCapableOf } from '@@/bits/app_can'
import { dir } from '@@/bits/current_dir'
import { __, p__ } from '@@/bits/intl'
import type { TableColumn } from '@@/bits/table_layout'
import {
  getColumnLetter,
  isCustomPropertyTableColumn,
  isNonPostAttributeTableColumn,
  shouldShowEndBorder,
} from '@@/bits/table_layout'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, { OzMenuRowsFocusStyle, OzMenuRowsSizePreset } from '@@/library/v4/components/OzMenuRows.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzPopover from '@@/library/v4/components/OzPopover.vue'
import postMessage from '@@/native_bridge/post_message'
import { createDomElement, createNativeMenuItem } from '@@/native_bridge/ui'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import Teleport from '@@/vuecomponents/Teleport.vue'
import { SettingsSubpanel } from '@@/vuexstore/modules/surface_settings'
import type { ComputePositionConfig } from '@floating-ui/vue'
import { flip, shift } from '@floating-ui/vue'
import { useElementBounding, useElementVisibility } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

defineProps<{
  tableColumnsToShow: TableColumn[]
  backgroundLuminance: 'light' | 'dark'
}>()

const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const surfacePermissionsStore = useSurfacePermissionsStore()
const surfaceStore = useSurfaceStore()

const isColumnHeaderEmpty = (column: TableColumn) => !column.label

// #region Popover menu visibility
const popoverFloatingConfig: ComputePositionConfig = { placement: 'bottom-end', middleware: [flip(), shift()] }

const xPopoverMenuButtonForColumn = (column: TableColumn) => {
  if (!surfacePermissionsStore.canUpdateWallSettings) return false
  if (isNonPostAttributeTableColumn(column)) return false
  return true
}

const columnHeaderPopoverMenuButtons = ref<Record<string, InstanceType<typeof OzPlainButton> | undefined>>({})
const assignColumnHeaderButtonRef = (column: TableColumn) => {
  return (el: InstanceType<typeof OzPlainButton> | null) => {
    if (el) {
      columnHeaderPopoverMenuButtons.value[column.field] = el
    }
  }
}
const popoverAnchorForColumn = (columnField: TableColumn['field']) => {
  const anchor = columnHeaderPopoverMenuButtons.value[columnField]
  if (!anchor) throw new Error('Popover anchor not found')
  return anchor?.$el
}

const columnHeaderPopoverMenuToShow = ref<TableColumn['field'] | null>(null)

const xPopoverMenuForColumn = (columnField: TableColumn['field']) => {
  return columnHeaderPopoverMenuToShow.value === columnField && popoverAnchorForColumn(columnField)
}

const openColumnHeaderPopover = (column: TableColumn) => {
  if (isAppCapableOf('tableLayout')) {
    openMobileAppColumnHeaderPopover(column)
    return
  }
  columnHeaderPopoverMenuToShow.value = column.field
}

function openMobileAppColumnHeaderPopover(column: TableColumn) {
  const element = createDomElement(popoverAnchorForColumn(column.field))

  const menuItems = [
    createNativeMenuItem({
      name: p__('Open settings panel for managing post properties', 'Manage fields'),
      icon: 'settings',
      key: 'manage_post_properties',
      style: 'default',
      actionHandledNatively: true,
      action: {
        message_type: 'manage_post_properties',
      },
    }),
  ]

  if (isCustomPropertyTableColumn(column)) {
    menuItems.unshift(
      createNativeMenuItem({
        name: p__('Open custom property settings panel', 'Edit field'),
        icon: 'pencil_outline',
        key: 'manage_custom_property',
        style: 'default',
        actionHandledNatively: true,
        action: {
          message_type: 'manage_custom_property',
          property_id: column.customProperty.id,
        },
      }),
    )

    if (isCustomPropertyTableColumn(column) && !isFirstCustomProperty(column) && !isLinkButtonColumn(column)) {
      menuItems.push(
        createNativeMenuItem({
          name:
            dir() === 'rtl'
              ? p__('Reorder custom property to the right', 'Move right')
              : p__('Reorder custom property to the left', 'Move left'),
          icon: 'arrow_left', // just use ltr icon; mobile app automatically handles rtl transformation
          key: 'move_custom_property_up',
          style: 'default',
          actionHandledNatively: false,
          action: {
            message_type: 'move_custom_property_up',
            payload: { property_id: column.customProperty.id },
          },
        }),
      )
    }

    if (
      isCustomPropertyTableColumn(column) &&
      !isLastCustomProperty(column) &&
      !isNextCustomPropertyLinkButton(column)
    ) {
      menuItems.push(
        createNativeMenuItem({
          name:
            dir() === 'rtl'
              ? p__('Reorder custom property to the left', 'Move left')
              : p__('Reorder custom property to the right', 'Move right'),
          icon: 'arrow-right', // just use ltr icon; mobile app automatically handles rtl transformation. also, casing is different for arrow left vs right.
          key: 'move_custom_property_down',
          style: 'default',
          actionHandledNatively: false,
          action: {
            message_type: 'move_custom_property_down',
            payload: { property_id: column.customProperty.id },
          },
        }),
      )
    }
  }

  // Opens native popover menu. See useWishMenuModalPresenter.ts on mobile app.
  postMessage({
    message_type: 'show_action_menu',
    payload: {
      manifest: {
        type: 'post_action_menu',
        title: __('Column menu'),
        element,
        items: [
          {
            key: 'group0',
            menuItems,
          },
        ],
      },
    },
  })
}

const closeColumnHeaderPopover = () => {
  columnHeaderPopoverMenuToShow.value = null
}
// #endregion

// #region Popover menu rows and behavior
const firstCustomProperty = computed(() => {
  return surfacePostPropertiesStore.wallCustomPostProperties[0]
})
const lastCustomProperty = computed(() => {
  return surfacePostPropertiesStore.wallCustomPostProperties[
    surfacePostPropertiesStore.wallCustomPostProperties.length - 1
  ]
})
const isFirstCustomProperty = (column: TableColumn) => {
  if (!isCustomPropertyTableColumn(column)) return false
  return column.customProperty.id === firstCustomProperty.value.id
}
const isLastCustomProperty = (column: TableColumn) => {
  if (!isCustomPropertyTableColumn(column)) return false
  return column.customProperty.id === lastCustomProperty.value.id
}
const isLinkButtonColumn = (column: TableColumn) => {
  if (!isCustomPropertyTableColumn(column)) return false
  return column.customProperty.data_type === 'link_button'
}
const isNextCustomPropertyLinkButton = (column: TableColumn) => {
  if (!isCustomPropertyTableColumn(column)) return false
  const currentIndex = surfacePostPropertiesStore.wallCustomPostProperties.findIndex(
    (prop) => prop.id === column.customProperty.id,
  )
  const nextProperty = surfacePostPropertiesStore.wallCustomPostProperties[currentIndex + 1]
  return nextProperty?.data_type === 'link_button'
}

type PopoverMenuRowEventPayload = 'manage_fields' | 'move_up' | 'move_down' | 'edit_field'

const popoverMenuRowsForColumn = (column: TableColumn) => {
  const rows: OzMenuRowsRow<PopoverMenuRowEventPayload>[] = [
    {
      text: p__('Open settings panel for managing posts', 'Manage fields'),
      icon: 'settings',
      eventPayload: 'manage_fields',
      testId: 'surfacePostsTableColumnHeaderManageFieldsButton',
    },
  ]

  if (isCustomPropertyTableColumn(column)) {
    rows.unshift({
      text: __('Edit field'),
      icon: 'pencil_outline',
      eventPayload: 'edit_field',
      testId: 'surfacePostsTableColumnHeaderEditFieldButton',
    })
  }

  if (isCustomPropertyTableColumn(column) && !isFirstCustomProperty(column) && !isLinkButtonColumn(column)) {
    rows.push({
      text:
        dir() === 'rtl'
          ? p__('Reorder custom property to the right', 'Move right')
          : p__('Reorder custom property to the left', 'Move left'),
      icon: 'arrow_left', // just use ltr icon, OzIcon will handle rtl transformation
      eventPayload: 'move_up',
      testId: 'surfacePostsTableColumnHeaderMoveUpLeftButton',
    })
  }

  if (isCustomPropertyTableColumn(column) && !isLastCustomProperty(column) && !isNextCustomPropertyLinkButton(column)) {
    rows.push({
      text:
        dir() === 'rtl'
          ? p__('Reorder custom property to the left', 'Move left')
          : p__('Reorder custom property to the right', 'Move right'),
      icon: 'arrow_right', // just use ltr icon, OzIcon will handle rtl transformation
      eventPayload: 'move_down',
      testId: 'surfacePostsTableColumnHeaderMoveDownRightButton',
    })
  }

  return rows
}
const handlePopoverMenuRowClick = async (column: TableColumn, payload: PopoverMenuRowEventPayload) => {
  if (payload === 'manage_fields') {
    surfacePostPropertiesStore.showSettingsPanelDirectly({
      settingsSubpanel: SettingsSubpanel.PostProperties,
    })
  } else if (payload === 'move_up' || payload === 'move_down') {
    if (!isCustomPropertyTableColumn(column)) return
    if (column.customProperty.id == null) return

    const direction = payload === 'move_up' ? 'up' : 'down'
    surfacePostPropertiesStore.moveCustomProperty(column.customProperty.id, direction)
  } else if (payload === 'edit_field') {
    if (!isCustomPropertyTableColumn(column)) return
    surfacePostPropertiesStore.openCustomPropertySettingsPanelDirectly(column.customProperty)
  }

  closeColumnHeaderPopover()
}
// #endregion

// #region sticky column header styling
const headerRef = ref<HTMLElement | null>(null)
const { top } = useElementBounding(headerRef)
const isVisible = useElementVisibility(headerRef)
const isSticky = ref(false)

watch([top, isVisible], ([newTop, newIsVisible]) => {
  if (newIsVisible) {
    isSticky.value = newTop <= 2
  } else {
    isSticky.value = false
  }
})

const { skinPrimaryVariantColorAsRGB, skinPrimaryTextColor, isSkinColorAvailable } = storeToRefs(surfaceStore)
const getColumnStyle = (column: TableColumn) => {
  return {
    width: `${column.width}px`,
    ...(column.label &&
      isSticky.value &&
      isSkinColorAvailable.value && {
        backgroundColor: skinPrimaryVariantColorAsRGB.value,
        color: skinPrimaryTextColor.value,
      }),
  }
}
// #endregion
</script>

<template>
  <div ref="headerRef" class="table-column-headers flex flex-row my-3">
    <div
      v-for="(column, index) in tableColumnsToShow"
      :key="column.field"
      :class="[
        'py-2 ps-2 pe-1',
        'text-start',
        'box-border',
        shouldShowEndBorder(column, index, tableColumnsToShow) && 'border-e-[0.5px] border-solid border-divider-gray',
        column.label && 'backdrop-blur shadow-elevation-1',
        column.label &&
          !isSticky && {
            'bg-white-100/[0.32] text-dark-text-100': backgroundLuminance === 'light',
            'bg-black/[0.24] text-light-text-100': backgroundLuminance === 'dark',
          },
        'transition-all duration-200',
        isSticky
          ? [
              // If sticking to top, only round bottom corners
              index === 0 && 'rounded-es-xl',
              index === tableColumnsToShow.length - 1 && 'rounded-ee-xl',
              tableColumnsToShow[index + 1]?.label === '' && 'rounded-ee-xl',
              tableColumnsToShow[index - 1]?.label === '' && 'rounded-es-xl',
            ]
          : [
              // If not sticking to top, round all corners
              index === 0 && 'rounded-s-xl',
              index === tableColumnsToShow.length - 1 && 'rounded-e-xl',
              tableColumnsToShow[index + 1]?.label === '' && 'rounded-e-xl',
              tableColumnsToShow[index - 1]?.label === '' && 'rounded-s-xl',
            ],
      ]"
      :style="getColumnStyle(column)"
      data-testid="surfacePostsTableColumnHeader"
    >
      <div v-if="!isColumnHeaderEmpty(column)" class="flex flex-row items-center justify-between gap-2">
        <div
          :class="['flex flex-row items-center gap-2', 'min-w-0 flex-1']"
          :data-testid="`${column.label}SurfacePostsTableColumnHeaderLabel`"
        >
          <span
            v-if="!isNonPostAttributeTableColumn(column)"
            :class="[
              'text-body-tiny font-semibold',
              backgroundLuminance === 'dark' ? 'text-light-text-300' : 'text-dark-text-300',
            ]"
          >
            {{ getColumnLetter(index - 1) /* -1 from index as first column is always row_number */ }}
          </span>

          <span
            dir="auto"
            :class="['text-body-small font-semibold truncate flex-1', isNonPostAttributeTableColumn(column) && 'ps-1']"
          >
            {{ column.label }}
          </span>
        </div>

        <OzPlainButton
          v-if="xPopoverMenuButtonForColumn(column)"
          :ref="assignColumnHeaderButtonRef(column)"
          :size-preset="OzPlainButtonSizePreset.H18px"
          :color-scheme="OzPlainButtonColorScheme.Bare"
          :dark-mode="backgroundLuminance === 'dark'"
          :data-testid="`${column.label}SurfacePostsTableColumnHeaderButton`"
          aria-haspopup="dialog"
          :aria-expanded="xPopoverMenuForColumn(column.field) ? 'true' : 'false'"
          :aria-label="__('Open menu for %{column} column', { column: column.label })"
          @click="openColumnHeaderPopover(column)"
        >
          <OzIcon
            name="more_vertical"
            :size="12"
            :class="[backgroundLuminance === 'dark' ? 'text-light-text-300' : 'text-dark-text-300']"
          />
        </OzPlainButton>
        <Teleport v-if="xPopoverMenuForColumn(column.field)" to="#surface-container">
          <OzPopover
            :anchor="popoverAnchorForColumn(column.field)"
            :config="popoverFloatingConfig"
            @scrim-click="closeColumnHeaderPopover"
            @scrim-esc="closeColumnHeaderPopover"
          >
            <div class="overflow-hidden rounded-2.5xl">
              <OzMenuRows
                :class="['w-64 py-1']"
                :size-preset="OzMenuRowsSizePreset.H48px"
                :focus-style="OzMenuRowsFocusStyle.Bubble200"
                :rows="popoverMenuRowsForColumn(column)"
                @select-row="(_, payload) => handlePopoverMenuRowClick(column, payload)"
              />
            </div>
          </OzPopover>
        </Teleport>
      </div>
    </div>
  </div>
</template>
